import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { startHeartbeat, stopHeartbeat } from './services/axiosInstance';
import IdleTimeoutManager from './components/IdleTimeoutManager';
import { useEffect } from 'react';
import { useAppSelector } from './hooks/redux.hooks';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import { ConfirmProvider } from './hooks/useConfirm';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toastify-custom.css';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/auth/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import ItemsPage from './pages/items/ItemsPage';
import CategoriesPage from './pages/categories/CategoriesPage';
import UnitsPage from './pages/units/UnitsPage';
import SuppliersPage from './pages/suppliers/SuppliersPage';
import PrivateRoute from './components/auth/PrivateRoute';
import CustomersPage from './pages/CustomersPage';
import WarehousesPage from './pages/WarehousesPage';
import BranchesPage from './pages/BranchesPage';
import StockLevelsPage from './pages/StockLevelsPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PurchaseInvoicesPage from './pages/PurchaseInvoicesPage';
import StockIssuesPage from './pages/StockIssuesPage';
import WarehouseTransfersPage from './pages/WarehouseTransfersPage';
import InventoryOpeningBalancesPage from './pages/inventoryOpeningBalances/InventoryOpeningBalancesPage';
import OpeningEntryPage from './pages/openingEntry/OpeningEntryPage';
import BranchTransfersPage from './pages/BranchTransfersPage';
import BranchIssuesPage from './pages/BranchIssuesPage';
import BranchIssueRequestsPage from './pages/BranchIssueRequestsPage';
import BranchPurchaseRequestsPage2 from './pages/BranchPurchaseRequestsPage2';
import StockCountsPage from './pages/StockCountsPage';
import StockMovementsPage from './pages/StockMovementsPage';
import PurchaseReturnsPage from './pages/PurchaseReturnsPage';
import CustodiesPage from './pages/CustodiesPage';
import EmployeeCustodySummaryPage from './pages/EmployeeCustodySummaryPage';
import EmployeesPage from './pages/EmployeesPage';
import TimeSheetPage from './pages/TimeSheetPage';
import TimeSheetReport from './pages/TimeSheetReport';
import DailyAttendancePage from './pages/DailyAttendancePage';
import PayrollsPage from './pages/PayrollsPage';
import PayrollReport from './pages/PayrollReport';
import HrAuditReportPage from './pages/HrAuditReportPage';
import CustodyPurchasesReportPage from './pages/CustodyPurchasesReportPage';
import AbsencesPage from './pages/AbsencesPage';
import DeductionsPage from './pages/DeductionsPage';
import WarningsPage from './pages/WarningsPage';
import LeavesPage from './pages/LeavesPage';
import Settings from './pages/Settings/Settings';
import ChartOfAccountsList from './pages/ChartOfAccounts/ChartOfAccountsList';
import ChartOfAccountForm from './pages/ChartOfAccounts/ChartOfAccountForm';
import ChartOfAccountsTree from './pages/ChartOfAccounts/ChartOfAccountsTree';
import ChartOfAccountView from './pages/ChartOfAccounts/ChartOfAccountView';
import JournalEntriesPage from './pages/JournalEntriesPage';
import CostCentersPage from './pages/CostCentersPage';
import EntryTypesPage from './pages/EntryTypesPage';
import AccountSelectorTestPage from './pages/AccountSelectorTestPage';
import SelectorsTestPage from './pages/SelectorsTestPage';
import FiscalPeriodsPage from './pages/FiscalPeriodsPage';
import ChartOfAccountsImportExportPage from './pages/ChartOfAccounts/ChartOfAccountsImportExportPage';
import CashPaymentsPage from './pages/accounting/CashPaymentsPage';
import CashPaymentsPageV2 from './pages/accounting/CashPaymentsPageV2';
import SmartEntryV2Page from './pages/accounting/SmartEntryV2Page';
import CashReceiptsPage from './pages/accounting/CashReceiptsPage';
import CheckReceiptsPage from './pages/accounting/CheckReceiptsPage';
import CheckPaymentsPage from './pages/accounting/CheckPaymentsPage';
import StockLevelsReport from './pages/reports/StockLevelsReport';
import PurchaseInvoicesReport from './pages/reports/PurchaseInvoicesReport';
import StockMovementsReport from './pages/reports/StockMovementsReport';
import PurchaseReturnsReport from './pages/reports/PurchaseReturnsReport';
import StockIssuesReport from './pages/reports/StockIssuesReport';
import WarehouseTransfersReport from './pages/reports/WarehouseTransfersReport';
import BranchTransfersReport from './pages/reports/BranchTransfersReport';
import StockCountsReport from './pages/reports/StockCountsReport';
import ExpiryReport from './pages/reports/ExpiryReport';
import SlowMovingReport from './pages/reports/SlowMovingReport';
import TrialBalancePage from './pages/reports/TrialBalancePage';
import OperationsMonitorPage from './pages/reports/OperationsMonitorPage';
import GeneralLedgerPage from './pages/reports/GeneralLedgerPage';
import FinancialStatementsPage from './pages/reports/Financialstatementspage';
import InventoryValuationReport from './pages/reports/InventoryValuationReport';
import FastMovingReport from './pages/reports/FastMovingReport';
import ItemMovementStatementReport from './pages/reports/ItemMovementStatementReport';
import AccountStatementReport from './pages/reports/AccountStatementReport';
import BranchProfitLossReport from './pages/reports/BranchProfitLossReport';
import CustodyStatementPage from './pages/reports/CustodyStatementPage';
import PurchaseOrdersReport from './pages/reports/PurchaseOrdersReport';
import ConsolidationReport from './pages/reports/ConsolidationReport';

// ✅ Daily Sales System
import ConceptsPage from './pages/ConceptsPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import DailySalesAccountConfigurationsPage from './pages/DailySalesAccountConfigurationsPage';
import DailySalesPage from './pages/DailySalesPage';

// ✅ Purchase Consolidation & Bulk Pricing
import PurchaseConsolidationPage from './pages/PurchaseConsolidationPage';
import BulkPricingPage from './pages/BulkPricingPage';

// ✅ Batch Posting
import BatchPostingPage from './pages/BatchPostingPage';

// ✅ Document Archive & Attachments
import DocumentArchivePage from './pages/DocumentArchivePage';
import DocumentAttachmentsPage from './pages/DocumentAttachmentsPage';

// ✅ NEW: Users & Permissions Pages
import UsersPage from './pages/Settings/UsersPage';
import RolesPage from './pages/Settings/RolesPage';
import UserPermissionsPage from './pages/Settings/UserPermissionsPage';
import UserFiscalYearsPage from './pages/Settings/UserFiscalYearsPage';
import RolePermissionsPage from './pages/Settings/RolePermissionsPage';

// ✅ Custody Settlements - تسوية العُهد
import CustodyExpenseCategoriesPage from './pages/CustodyExpenseCategoriesPage';
import CustodySettlementsPage from './pages/CustodySettlementsPage';
import BakeryPurchasePage from './pages/BakeryPurchasePage';

// ✅ Fixed Assets & Depreciation
import FixedAssetCategoriesPage from './pages/fixedAssets/FixedAssetCategoriesPage';
import FixedAssetsPage from './pages/fixedAssets/FixedAssetsPage';
import FixedAssetSchedulePage from './pages/fixedAssets/FixedAssetSchedulePage';
import DepreciationRunPage from './pages/fixedAssets/DepreciationRunPage';
import FixedAssetsReportPage from './pages/fixedAssets/FixedAssetsReportPage';
import DataIntegrityReportPage from './pages/reports/DataIntegrityReportPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import InventoryHealthHubPage from './pages/admin/InventoryHealthHubPage';
import SmartEntryDemoPage from './pages/admin/SmartEntryDemoPage';
import InventoryRebalancePage from './pages/tools/InventoryRebalancePage';
import BranchReceiptsReport from './pages/reports/BranchReceiptsReport';
import FinancialAnalysisReport from './pages/reports/FinancialAnalysisReport';
import CostVerificationReport from './pages/reports/CostVerificationReport';
import ActualStockReport from './pages/reports/ActualStockReport';
import WarehouseDispatchReport from './pages/reports/WarehouseDispatchReport';
import ItemLifecycleReport from './pages/reports/ItemLifecycleReport';
import ItemsUnitsReport from './pages/reports/ItemsUnitsReport';
import BPRWorkflowReport from './pages/reports/BPRWorkflowReport';
import DailySalesSummaryReport from './pages/reports/DailySalesSummaryReport';
import InternalIssuesPage from './pages/InternalIssuesPage';

// Reads language context and renders a RTL-aware ToastContainer
function AppToast() {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    return (
        <ToastContainer
            position={isRTL ? 'top-left' : 'top-right'}
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={isRTL}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
        />
    );
}

function HeartbeatManager() {
    const isAuthenticated = useAppSelector(s => s.auth.isAuthenticated);
    useEffect(() => {
        if (isAuthenticated) startHeartbeat();
        else stopHeartbeat();
        return () => stopHeartbeat();
    }, [isAuthenticated]);
    return null;
}

function App() {
    return (
        <Provider store={store}>
            <HeartbeatManager />
            <ThemeProvider>
                <LanguageProvider>
                    <ConfirmProvider>
                    <ErrorBoundary>
                    <NotificationProvider>
                    <ChatProvider>
                    <Router>
                        <IdleTimeoutManager />
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/login" element={<LoginPage />} />

                            {/* Protected Routes */}
                            <Route
                                element={
                                    <PrivateRoute>
                                        <DashboardLayout />
                                    </PrivateRoute>
                                }
                            >
                                <Route
                                    path="/"
                                    element={
                                        <Navigate to="/dashboard" replace />
                                    }
                                />
                                <Route
                                    path="/dashboard"
                                    element={<DashboardPage />}
                                />
                                <Route path="/items" element={<ItemsPage />} />
                                <Route
                                    path="/categories"
                                    element={<CategoriesPage />}
                                />
                                <Route path="/units" element={<UnitsPage />} />
                                <Route
                                    path="/suppliers"
                                    element={<SuppliersPage />}
                                />
                                <Route
                                    path="/customers"
                                    element={<CustomersPage />}
                                />
                                <Route
                                    path="/warehouses"
                                    element={<WarehousesPage />}
                                />
                                <Route
                                    path="/branches"
                                    element={<BranchesPage />}
                                />
                                <Route
                                    path="/stock-levels"
                                    element={<StockLevelsPage />}
                                />
                                <Route
                                    path="/purchase-orders"
                                    element={<PurchaseOrdersPage />}
                                />
                                <Route
                                    path="/purchase-invoices"
                                    element={<PurchaseInvoicesPage />}
                                />
                                <Route
                                    path="/stock-issues"
                                    element={<StockIssuesPage />}
                                />
                                <Route
                                    path="/warehouse-transfers"
                                    element={<WarehouseTransfersPage />}
                                />
                                <Route
                                    path="/inventory-opening-balances"
                                    element={<InventoryOpeningBalancesPage />}
                                />
                                <Route
                                    path="/opening-entries"
                                    element={<OpeningEntryPage />}
                                />
                                <Route
                                    path="/branch-transfers"
                                    element={<BranchTransfersPage />}
                                />
                                <Route
                                    path="/branch-issues"
                                    element={<BranchIssuesPage />}
                                />
                                <Route
                                    path="/branch-issue-requests"
                                    element={<BranchIssueRequestsPage />}
                                />
                                <Route
                                    path="/branch-purchase-requests"
                                    element={<BranchPurchaseRequestsPage2 />}
                                />
                                <Route
                                    path="/stock-counts"
                                    element={<StockCountsPage />}
                                />
                                <Route
                                    path="/stock-movements"
                                    element={<StockMovementsPage />}
                                />
                                <Route
                                    path="/purchase-returns"
                                    element={<PurchaseReturnsPage />}
                                />
                                <Route
                                    path="/purchase-consolidation"
                                    element={<PurchaseConsolidationPage />}
                                />
                                <Route
                                    path="/bulk-pricing"
                                    element={<BulkPricingPage />}
                                />
                                <Route
                                    path="/custodies"
                                    element={<CustodiesPage />}
                                />
                                <Route
                                    path="/employee-custody-summary"
                                    element={<EmployeeCustodySummaryPage />}
                                />
                                <Route
                                    path="/employees"
                                    element={<EmployeesPage />}
                                />
                                <Route
                                    path="/timesheet"
                                    element={<TimeSheetPage />}
                                />
                                <Route
                                    path="/timesheet-report"
                                    element={<TimeSheetReport />}
                                />
                                <Route
                                    path="/daily-attendance"
                                    element={<DailyAttendancePage />}
                                />
                                <Route
                                    path="/payrolls"
                                    element={<PayrollsPage />}
                                />
                                <Route
                                    path="/payroll-report"
                                    element={<PayrollReport />}
                                />
                                <Route
                                    path="/hr-audit"
                                    element={<HrAuditReportPage />}
                                />
                                <Route
                                    path="/custody-purchases-report"
                                    element={<CustodyPurchasesReportPage />}
                                />
                                <Route
                                    path="/leaves"
                                    element={<LeavesPage />}
                                />
                                <Route
                                    path="/absences"
                                    element={<AbsencesPage />}
                                />
                                <Route
                                    path="/deductions"
                                    element={<DeductionsPage />}
                                />
                                <Route
                                    path="/warnings"
                                    element={<WarningsPage />}
                                />
                                <Route
                                    path="/settings"
                                    element={<Settings />}
                                />
                                <Route
                                    path="/journal-entries"
                                    element={<JournalEntriesPage />}
                                />
                                <Route
                                    path="/accounting/cash-payments"
                                    element={<CashPaymentsPage />}
                                />
                                <Route
                                    path="/smart-entry-v2"
                                    element={<SmartEntryV2Page />}
                                />
                                <Route
                                    path="/cash-payments-v2"
                                    element={<CashPaymentsPageV2 />}
                                />
                                <Route
                                    path="/accounting/check-payments"
                                    element={<CheckPaymentsPage />}
                                />
                                <Route
                                    path="/accounting/cash-receipts"
                                    element={<CashReceiptsPage />}
                                />
                                <Route
                                    path="/accounting/check-receipts"
                                    element={<CheckReceiptsPage />}
                                />
                                <Route
                                    path="/test-account-selector"
                                    element={<AccountSelectorTestPage />}
                                />
                                <Route
                                    path="/test-selectors"
                                    element={<SelectorsTestPage />}
                                />
                                <Route
                                    path="/fiscal-periods"
                                    element={<FiscalPeriodsPage />}
                                />

                                {/* Chart of Accounts */}
                                <Route
                                    path="/chart-of-accounts"
                                    element={<ChartOfAccountsList />}
                                />
                                <Route
                                    path="/chart-of-accounts/create"
                                    element={<ChartOfAccountForm />}
                                />
                                <Route
                                    path="/chart-of-accounts/edit/:id"
                                    element={<ChartOfAccountForm />}
                                />
                                <Route
                                    path="/chart-of-accounts/view/:id"
                                    element={<ChartOfAccountView />}
                                />
                                <Route
                                    path="/chart-of-accounts/tree"
                                    element={<ChartOfAccountsTree />}
                                />
                                <Route
                                    path="/chart-of-accounts/import-export"
                                    element={
                                        <ChartOfAccountsImportExportPage />
                                    }
                                />

                                {/* Cost Centers */}
                                <Route
                                    path="/cost-centers"
                                    element={<CostCentersPage />}
                                />

                                {/* Entry Types */}
                                <Route
                                    path="/entry-types"
                                    element={<EntryTypesPage />}
                                />

                                {/* ✅ Daily Sales System */}
                                <Route
                                    path="/concepts"
                                    element={<ConceptsPage />}
                                />
                                <Route
                                    path="/payment-methods"
                                    element={<PaymentMethodsPage />}
                                />
				<Route
 				     path="/account-configurations"
				     element={<DailySalesAccountConfigurationsPage />}
				/>
				<Route
  				     path="/daily-sales"
 				     element={<DailySalesPage />}
				/>

                                {/* ✅ NEW: Users & Permissions Routes */}
                                <Route
                                    path="/settings/users"
                                    element={<UsersPage />}
                                />
                                <Route
                                    path="/settings/roles"
                                    element={<RolesPage />}
                                />
                                <Route
                                    path="/settings/user-permissions/:userId"
                                    element={<UserPermissionsPage />}
                                />
                                <Route
                                    path="/settings/user-fiscal-years/:userId"
                                    element={<UserFiscalYearsPage />}
                                />
                                <Route
                                    path="/settings/role-permissions/:roleId"
                                    element={<RolePermissionsPage />}
                                />

                                {/* Batch Posting */}
                                <Route
                                    path="/batch-posting"
                                    element={<BatchPostingPage />}
                                />

                                {/* ✅ Custody Settlements - تسوية العُهد */}
                                <Route
                                    path="/custody-expense-categories"
                                    element={<CustodyExpenseCategoriesPage />}
                                />
                                <Route
                                    path="/custody-settlements"
                                    element={<CustodySettlementsPage />}
                                />

                                {/* ✅ Bakery Purchases */}
                                <Route
                                    path="/bakery-purchases"
                                    element={<BakeryPurchasePage />}
                                />

                                {/* ✅ Document Archive & Attachments */}
                                <Route
                                    path="/transaction-log"
                                    element={<DocumentArchivePage />}
                                />
                                <Route
                                    path="/document-attachments"
                                    element={<DocumentAttachmentsPage />}
                                />

                                {/* Reports */}
                                <Route
                                    path="/reports/stock-levels"
                                    element={<StockLevelsReport />}
                                />
                                <Route
                                    path="/reports/purchase-orders"
                                    element={<PurchaseOrdersReport />}
                                />
                                <Route
                                    path="/reports/purchase-invoices"
                                    element={<PurchaseInvoicesReport />}
                                />
                                <Route
                                    path="/reports/consolidation"
                                    element={<ConsolidationReport />}
                                />
                                <Route
                                    path="/reports/stock-movements"
                                    element={<StockMovementsReport />}
                                />
                                <Route
                                    path="/reports/purchase-returns"
                                    element={<PurchaseReturnsReport />}
                                />
                                <Route
                                    path="/reports/stock-issues"
                                    element={<StockIssuesReport />}
                                />
                                <Route
                                    path="/reports/warehouse-transfers"
                                    element={<WarehouseTransfersReport />}
                                />
                                <Route
                                    path="/reports/branch-transfers"
                                    element={<BranchTransfersReport />}
                                />
                                <Route
                                    path="/reports/stock-counts"
                                    element={<StockCountsReport />}
                                />
                                <Route
                                    path="/reports/expiry"
                                    element={<ExpiryReport />}
                                />
                                <Route
                                    path="/reports/slow-moving"
                                    element={<SlowMovingReport />}
                                />
                                <Route
                                    path="/reports/inventory-valuation"
                                    element={<InventoryValuationReport />}
                                />
                                <Route
                                    path="/reports/fast-moving"
                                    element={<FastMovingReport />}
                                />
                                <Route
                                    path="/reports/item-movement-statement"
                                    element={<ItemMovementStatementReport />}
                                />
                                <Route
                                    path="/reports/trial-balance"
                                    element={<TrialBalancePage />}
                                />
                                <Route
                                    path="/reports/operations-monitor"
                                    element={<OperationsMonitorPage />}
                                />
                                <Route
                                    path="/reports/general-ledger"
                                    element={<GeneralLedgerPage />}
                                />
                                <Route
                                    path="/reports/financial-statements"
                                    element={<FinancialStatementsPage />}
                                />
                                <Route
                                    path="/reports/branch-profit-loss"
                                    element={<BranchProfitLossReport />}
                                />
                                <Route
                                    path="/reports/account-statement"
                                    element={<AccountStatementReport />}
                                />
                                <Route
                                    path="/reports/custody-statement"
                                    element={<CustodyStatementPage />}
                                />

                                {/* ── Fixed Assets & Depreciation ── */}
                                <Route path="/fixed-asset-categories" element={<FixedAssetCategoriesPage />} />
                                <Route path="/fixed-assets" element={<FixedAssetsPage />} />
                                <Route path="/fixed-assets/:id/schedule" element={<FixedAssetSchedulePage />} />
                                <Route path="/depreciation-run" element={<DepreciationRunPage />} />
                                <Route path="/reports/fixed-assets" element={<FixedAssetsReportPage />} />
                                <Route path="/reports/data-integrity" element={<DataIntegrityReportPage />} />
                                <Route path="/admin/audit-log" element={<AuditLogPage />} />
                                <Route path="/admin/inventory-health-hub" element={<InventoryHealthHubPage />} />
                                <Route path="/admin/smart-entry-demo" element={<SmartEntryDemoPage />} />
                                <Route path="/tools/inventory-rebalance" element={<InventoryRebalancePage />} />
                                <Route path="/reports/branch-receipts" element={<BranchReceiptsReport />} />
                                <Route path="/reports/financial-analysis" element={<FinancialAnalysisReport />} />
                                <Route path="/reports/cost-verification" element={<CostVerificationReport />} />
                                <Route path="/reports/actual-stock" element={<ActualStockReport />} />
                                <Route path="/reports/warehouse-dispatch" element={<WarehouseDispatchReport />} />
                                <Route path="/reports/item-lifecycle" element={<ItemLifecycleReport />} />
                                <Route path="/reports/items-units" element={<ItemsUnitsReport />} />
                                <Route path="/reports/bpr-workflow" element={<BPRWorkflowReport />} />
                                <Route path="/reports/daily-sales-summary" element={<DailySalesSummaryReport />} />
                                <Route path="/internal-issues" element={<InternalIssuesPage />} />
                                <Route path="/reports/bpr-workflow" element={<BPRWorkflowReport />} />

                                {/* Category index redirects → first sub-page */}
                                <Route path="/purchases" element={<Navigate to="/purchase-orders" replace />} />
                                <Route path="/inventory" element={<Navigate to="/stock-levels" replace />} />
                                <Route path="/accounting" element={<Navigate to="/journal-entries" replace />} />
                                <Route path="/reports" element={<Navigate to="/reports/stock-levels" replace />} />
                            </Route>

                            {/* 404 Route */}
                            <Route
                                path="*"
                                element={<Navigate to="/dashboard" replace />}
                            />
                        </Routes>
                    </Router>
                    </ChatProvider>
                    </NotificationProvider>
                    </ErrorBoundary>
                    </ConfirmProvider>

                    {/* 🍞 Toast Container — RTL-aware */}
                    <AppToast />
                </LanguageProvider>
            </ThemeProvider>
        </Provider>
    );
}

export default App;